import React from "react";
import { useParams, Navigate } from "react-router-dom";
import { useI18n, NLink } from "@/i18n/I18nProvider";
import { ArrowLeft, ArrowRight } from "lucide-react";

const SPORTS_HERO = {
  soccer: "https://images.unsplash.com/photo-1517466787929-bc90951d0974?w=1800&q=80",
  golf: "https://images.unsplash.com/photo-1535131749006-b7f58c99034b?w=1800&q=80",
  tennis: "https://images.unsplash.com/photo-1554068865-24cecd4e34b8?w=1800&q=80",
  swimming: "https://images.unsplash.com/photo-1530549387789-4c1017266635?w=1800&q=80",
  volleyball: "https://images.unsplash.com/photo-1612872087720-bb876e2e67d1?w=1800&q=80",
  basketball: "https://images.unsplash.com/photo-1546519638-68e109498ffc?w=1800&q=80",
  "track-field": "https://images.unsplash.com/photo-1461896836934-ffe607ba8211?w=1800&q=80",
};

const FAQS = {
  en: [
    { q: "At what age should we begin?", a: "Most families engage Northcrest between the athlete's 14th and 16th year, though earlier conversations are welcomed." },
    { q: "Is a scholarship guaranteed?", a: "No advisor can guarantee a scholarship. Northcrest engineers the strongest possible profile and pathway." },
    { q: "Do you work with all NCAA divisions?", a: "Yes — NCAA Divisions I, II, III, NAIA and NJCAA, depending on the athlete's profile and ambition." },
  ],
  de: [
    { q: "Wann sollten wir beginnen?", a: "Die meisten Familien engagieren Northcrest zwischen dem 14. und 16. Lebensjahr." },
    { q: "Ist ein Stipendium garantiert?", a: "Kein Berater kann ein Stipendium garantieren. Wir gestalten das stärkste mögliche Profil." },
    { q: "Arbeiten Sie mit allen NCAA-Divisionen?", a: "Ja — NCAA Division I, II, III, NAIA und NJCAA." },
  ],
  pt: [
    { q: "Em que idade devemos começar?", a: "A maioria das famílias contacta a Northcrest entre os 14 e os 16 anos." },
    { q: "É garantida uma bolsa?", a: "Nenhum consultor pode garantir uma bolsa. Construímos o perfil mais forte possível." },
    { q: "Trabalham com todas as divisões da NCAA?", a: "Sim — NCAA Divisão I, II, III, NAIA e NJCAA." },
  ],
};

const SportDetail = () => {
  const { slug } = useParams();
  const { t, lang } = useI18n();
  const sport = t.sports.list.find((s) => s.slug === slug);
  if (!sport) return <Navigate to={`/${lang}/sports`} replace />;

  const hero = SPORTS_HERO[slug];
  const labels = t.sports.detail;
  const faqs = FAQS[lang] || FAQS.en;

  return (
    <>
      <section className="relative grain bg-[#0F1B2D] text-white pt-36 pb-24 overflow-hidden min-h-[60vh] flex items-end">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `linear-gradient(rgba(15,27,45,0.5), rgba(15,27,45,0.9)), url(${hero})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 w-full">
          <NLink
            to="/sports"
            className="inline-flex items-center gap-2 text-[#C4A46A] text-[11px] uppercase tracking-[0.24em] font-bold mb-8 hover:text-white transition-colors"
            data-testid="sport-back"
          >
            <ArrowLeft size={14} /> {t.common.back}
          </NLink>
          <div className="overline text-[#C4A46A]">{t.sports.title}</div>
          <h1 className="mt-4 font-display text-5xl lg:text-7xl tracking-tight">{sport.t}</h1>
          <span className="gold-rule mt-8 block" />
        </div>
      </section>

      <section className="bg-[#F4F0E8]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7 space-y-14">
            <div>
              <div className="overline">{labels.overview}</div>
              <h2 className="mt-3 font-display text-3xl text-[#0F1B2D] tracking-tight">{labels.overview}</h2>
              <span className="gold-rule mt-4 block" />
              <p className="mt-6 text-[#5E6470] text-lg leading-relaxed">
                {sport.t} — a Northcrest advisory pathway designed around the unique athletic, academic and personal profile of each European student-athlete pursuing the American collegiate route.
              </p>
            </div>
            <div>
              <div className="overline">{labels.opportunities}</div>
              <h2 className="mt-3 font-display text-3xl text-[#0F1B2D] tracking-tight">{labels.opportunities}</h2>
              <span className="gold-rule mt-4 block" />
              <ul className="mt-6 space-y-3 text-[#5E6470] leading-relaxed">
                <li>· NCAA Division I, II and III programs</li>
                <li>· NAIA and NJCAA pathways</li>
                <li>· Academic and athletic scholarship consideration</li>
                <li>· Showcases, camps and direct coach exposure</li>
              </ul>
            </div>
            <div>
              <div className="overline">{labels.pathways}</div>
              <h2 className="mt-3 font-display text-3xl text-[#0F1B2D] tracking-tight">{labels.pathways}</h2>
              <span className="gold-rule mt-4 block" />
              <p className="mt-6 text-[#5E6470] leading-relaxed">
                Northcrest curates a shortlist of programs aligned with the athlete&apos;s fit, level and academic ambition — across the full breadth of American collegiate athletics.
              </p>
            </div>
          </div>
          <aside className="lg:col-span-5">
            <div className="bg-[#0F1B2D] text-white p-10">
              <div className="overline text-[#C4A46A]">{labels.timeline}</div>
              <h3 className="mt-3 font-display text-2xl tracking-tight">{labels.timeline}</h3>
              <span className="gold-rule mt-4 block" />
              <ul className="mt-8 space-y-7">
                {labels.timelineSteps.map((s) => (
                  <li key={s.y} className="flex gap-5">
                    <div className="font-display text-[#C4A46A] text-lg whitespace-nowrap">{s.y}</div>
                    <div>
                      <div className="font-display text-white text-lg tracking-tight">{s.t}</div>
                      <div className="mt-1 text-white/65 text-sm">{s.d}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>

      <section className="bg-[#0F1B2D] text-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24">
          <div className="overline text-[#C4A46A]">{labels.faqs}</div>
          <h2 className="mt-3 font-display text-3xl lg:text-4xl tracking-tight">{labels.faqs}</h2>
          <span className="gold-rule mt-6 block" />
          <div className="mt-12 divide-y divide-white/15">
            {faqs.map((f, i) => (
              <div key={f.q} className="py-8" data-testid={`faq-${i}`}>
                <h3 className="font-display text-xl tracking-tight">{f.q}</h3>
                <p className="mt-3 text-white/70 leading-relaxed max-w-3xl">{f.a}</p>
              </div>
            ))}
          </div>
          <div className="mt-12">
            <NLink
              to="/contact"
              className="inline-flex items-center gap-3 px-8 py-4 bg-[#C4A46A] text-[#0F1B2D] text-[12px] tracking-[0.24em] uppercase font-bold hover:bg-[#A38755] transition-colors"
              data-testid="sport-detail-cta"
            >
              {t.nav.bookCta} <ArrowRight size={16} />
            </NLink>
          </div>
        </div>
      </section>
    </>
  );
};

export default SportDetail;
